import React, { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import AutoIdGeneratorTable from '../../Components/AutoIdGeneratorTable/AutoIdGenerator';
import * as action from './Store/SystemMasters.action';
import { filterCustomAttributesDropdownAndTextType } from '../../Components/helper';

function AutoGenerator() {
  const reducer = useSelector((estate) => estate);
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const { SystemMastersDetails, UserDetails } = reducer;
  const { autoIdMaster, productIdentifierList, brandList, customAttributeList, pcpCustomAttribute } = SystemMastersDetails;
  const { userInfo } = UserDetails;
  const initialState = {
    buttonDownIdGenerator: true,
    isLoading: false,
    showConfirmation: false,
    isDesabledAutoId: false,
    isLoadingConf: false,
    isProductNumberEdit: false,
  };
  const [state, setState] = useState(initialState);
  const [childState, setChildState] = useState({ autoIdModule: [], productIdentifier: [], isProductNumberEdit: false });
  const AutoGeneratorUrl = '/auto-id-generator-master';
  const loggedInUserId = userInfo.userId;

  const customAttributeData = useMemo(() => {
    return filterCustomAttributesDropdownAndTextType(customAttributeList)?.filter((vl) => vl?.type === 'dropdown') || [];
  }, [customAttributeList]);

  const multiCustomAttributeData = useMemo(() => {
    return pcpCustomAttribute || [];
  }, [pcpCustomAttribute]);

  const productIdentifierData = useMemo(() => {
    return productIdentifierList || [];
  }, [productIdentifierList]);

  const autoIdData = useMemo(() => {
    return autoIdMaster || [];
  }, [autoIdMaster]);

  const brandData = useMemo(() => {
    return brandList || [];
  }, [brandList]);

  const getProductIdentifier = async () => {
    dispatch(action.fetchProductIdentifier());
    dispatch(action.getCustomAttributeFreeText());
    dispatch(action.getMultiCustomAttribute());
  };

  const callApi = async () => {
    setState((prevState) => ({
      ...prevState,
      buttonDownIdGenerator: !prevState.buttonDownIdGenerator,
      isLoading: true,
    }));
    await dispatch(action.AutoIdGenerator());
    getProductIdentifier();
    setState((prevState) => ({
      ...prevState,
      isLoading: false,
    }));
  };

  const onSave = async () => {
    setState((prevState) => ({ ...prevState, isDesabledAutoId: true }));

    try {
      // Helper function to convert to string array
      const toStringArray = (arr) => arr?.map((item) => (typeof item === 'string' ? item : item?.value)) || [];

      // Validate autoIdModule data
      const autoModuleData = childState?.autoIdModule?.map((item) => {
        let errorMsg = '';
        if ((item.refCode === 'Product_Number' || item.refCode === 'Product_Number_Universal') && item.isModuleActive) {
          const selectedValues = toStringArray(item?.selectedChildProperties);
          const counterOptions = selectedValues.filter((val) => ['3', '5'].includes(val));

          // Counter is compulsory
          if (counterOptions.length === 0) {
            errorMsg = t('PRODUCT_IDENTIFIER_SYSTEM_MASTER.SELECT_COUNTER');
          } else if (!item?.counterNo) {
            errorMsg = t('PRODUCT_IDENTIFIER_SYSTEM_MASTER.ENTER_COUNTER_NO');
          } else {
            // Validate counter length based on selected counter type
            const counterValue = counterOptions[0];
            const requiredLength = counterValue === '3' ? 3 : 5;

            if (item.counterNo.length !== requiredLength) {
              errorMsg = `Counter number must be exactly ${requiredLength} characters`;
            }
          }
        }
        return { ...item, err: errorMsg };
      });

      // Validate productIdentifier data
      const productIdentifierData = childState?.productIdentifier?.map((obj) => {
        if (!obj.isModuleActive) return { ...obj, err: null };

        const selectedValues = toStringArray(obj?.selectedChildProperties);
        let errorMsg = null;

        if (selectedValues.length === 0) {
          errorMsg = t('PRODUCT_IDENTIFIER_SYSTEM_MASTER.SELECT_ONE_OPTION');
        } else {
          const counterOptions = selectedValues.filter((val) => ['3', '5', '10', '11', '12', '13'].includes(val));
          if (counterOptions.length > 0) {
            if (!obj?.counterNo) {
              errorMsg = t('PRODUCT_IDENTIFIER_SYSTEM_MASTER.ENTER_COUNTER_NO');
            } else {
              const requiredLength = Math.max(...counterOptions.map((val) => parseInt(val)));
              if (obj.counterNo.length !== requiredLength) {
                errorMsg = `Counter number must be exactly ${requiredLength} characters`;
              }
            }
          }
        }
        return { ...obj, err: errorMsg };
      });

      // Check for validation errors
      const hasErrors = autoModuleData?.some((item) => item.err) || productIdentifierData?.some((item) => item.err && item.isModuleActive);

      if (hasErrors) {
        setChildState((prevState) => ({
          ...prevState,
          autoIdModule: autoModuleData,
          productIdentifier: productIdentifierData,
        }));
        return;
      }

      // Filter and prepare data for save
      const productNumberData = autoModuleData?.filter((obj) => obj.refCode === 'Product_Number' || obj.refCode === 'Product_Number_Universal') || [];
      const updateData = [...productNumberData, ...productIdentifierData];

      if (isEmpty(updateData) || updateData?.some((item) => !item.name?.trim())) {
        return;
      }

      // Transform data for API
      const autoIdPayload = updateData.map((item) => {
        // Convert to string arrays
        let selectedProps = toStringArray(item?.selectedChildProperties);
        const childProps = toStringArray(item?.childProperties);

        // Remove unnecessary dashes
        const hasNonDashNonCounter = selectedProps.some((prop) => prop !== '-' && prop !== 'counter');
        if (!hasNonDashNonCounter) {
          selectedProps = selectedProps.filter((prop) => prop !== '-');
        }

        // Build base payload
        const payload = {
          ...(item?._id && { _id: item._id }),
          isActive: item?.isActive,
          isModuleActive: item?.isModuleActive || false,
          name: item?.name?.trim(),
          refCode: item?.refCode,
          childProperties: childProps,
          selectedChildProperties: selectedProps,
        };

        // For Product_Number and Product_Number_Universal, use separate counter fields
        if (item.refCode === 'Product_Number' || item.refCode === 'Product_Number_Universal') {
          // Determine which counter type is selected
          const selectedCounter = item?.selectedChildProperties?.find((prop) => prop?.isCounterField === true || ['3', '5'].includes(prop?.value));
          const counterType = selectedCounter?.value;

          // Always send separate counter fields
          payload.counterNo3 = item?.counterNo3 || '000';
          payload.counterNo5 = item?.counterNo5 || item?.counterNo || '00000';

          // Set counterSet flags based on selected counter type
          // If 3-digit is selected, set counterSet3 to true (preserve existing true values)
          // If 5-digit is selected, set counterSet5 to true (preserve existing true values)
          if (counterType === '3') {
            payload.counterSet3 = true;
            payload.counterSet5 = item?.counterSet5 !== undefined ? item.counterSet5 : false;
            payload.counterNo = item?.counterNo3 || item?.counterNo || '000';
            payload.counterSet = true;
          } else {
            // Default to 5-digit
            payload.counterSet3 = item?.counterSet3 !== undefined ? item.counterSet3 : false;
            payload.counterSet5 = true;
            payload.counterNo = item?.counterNo5 || item?.counterNo || '00000';
            payload.counterSet = true;
          }
        } else {
          // For Product_Identifier, use legacy counterNo and counterSet
          const counterSet = Boolean(item.counterNo);
          payload.counterNo = item?.counterNo;
          payload.counterSet = counterSet;
        }

        // Add brandId for Product_Number (brand-specific records)
        if (item.refCode === 'Product_Number' && item.brandId) {
          payload.brandId = item.brandId;
        }

        // Add initialCounterValues if present
        if (item?.initialCounterValues) {
          payload.initialCounterValues = item.initialCounterValues;
        }

        // Add productIdentifierId for Product_Identifier records
        if (item?.productIdentifierId) {
          payload.productIdentifierId = item.productIdentifierId;
        }

        return payload;
      });

      const saveParams = {
        loggedInUserId,
        aliasName: JSON.stringify(autoIdPayload),
        title: AutoGeneratorUrl,
      };

      await dispatch(action.saveToSystemMasterAutoId(saveParams, t));
      await dispatch(action.AutoIdGenerator());
    } catch (error) {
      console.error('Error saving Auto ID Generator:', error);
    } finally {
      setState((prevState) => ({ ...prevState, isDesabledAutoId: false }));
    }
  };

  return (
    <AutoIdGeneratorTable
      callApi={callApi}
      buttonDown={state?.buttonDownIdGenerator}
      isLoading={state?.isLoading}
      isDesabled={state?.isDesabledAutoId}
      autoIdData={autoIdData}
      onSave={onSave}
      ProductIdentifierData={productIdentifierData}
      state={childState}
      setState={setChildState}
      brandData={brandData}
      customAttributeData={customAttributeData}
      multiCustomAttributeData={multiCustomAttributeData}
    />
  );
}

export default AutoGenerator;
